
import React, { memo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import StarRating from '@/components/ui/StarRating';

// Estes tres NAO podem virar base64. O <SocialProof/> abaixo renderiza duas
// vezes — mobile e desktop — entao cada avatar embutido sai DUPLICADO no HTML
// pre-renderizado: 9,7 KB, ~5 KB de pura duplicata. Como arquivo, as duas
// copias apontam para a mesma URL e o browser busca uma vez so.
// A exclusao esta no `assetsInlineLimit` do vite.config.ts, que e onde o Vite 5
// decide isso (`?no-inline` so existe a partir do Vite 6). Ver P13.
import avaliacaoPhoto1 from '@/assets/paciente-avaliacao-cirurgia-1.webp';
import avaliacaoPhoto2 from '@/assets/paciente-avaliacao-cirurgia-2.webp';
import avaliacaoPhoto3 from '@/assets/paciente-avaliacao-cirurgia-3.webp';
import { useParallax } from '@/hooks/useParallax';

// Hero images served from /public — enables <link rel="preload"> in HTML head
// so the browser discovers the LCP image during HTML parse, not after JS execution.
const heroBanner = '/cirurgiao-plastico-curitiba-hero-desktop.webp';
const heroBannerMobile = '/cirurgiao-plastico-curitiba-hero-mobile.webp';

interface HeroSectionProps {
  scrollToSection: (sectionId: string) => void;
  onOpenBooking: () => void;
  overrideTitle?: React.ReactNode;
  overrideSubtitle?: string;
  overrideDescription?: string;
  overrideCta?: string;
  overrideBanner?: string;
  overrideBannerMobile?: string;
  hideSecondaryButton?: boolean;
}

const SocialProof = () =>
<div className="flex flex-col items-center lg:items-start gap-3">
    {/* Stars and text */}
    <div className="flex items-center gap-2">
      <StarRating className="flex" />
      <span className="text-sm text-white font-medium">+1000 Pacientes atendidos</span>
    </div>
    
    {/* Avatar circles */}
    <div className="flex items-center">
      <div className="flex -space-x-3">
        {[avaliacaoPhoto1, avaliacaoPhoto2, avaliacaoPhoto3].map((photo, i) => (
          <img
            key={i}
            src={photo}
            alt={`Paciente do Dr. Luis Brunello ${i + 1}`}
            title={`Paciente do Dr. Luis Brunello ${i + 1}`}
            className="w-10 h-10 rounded-full border-2 border-dark-subtle ring-2 ring-dark-base object-cover"
            width="40"
            height="40"
            loading="eager"
            decoding="async"
          />
        ))}
        <div className="w-10 h-10 rounded-full border-2 border-dark-subtle bg-dark-elevated flex items-center justify-center text-xs text-light-primary font-medium ring-2 ring-dark-base">
          +50
        </div>
      </div>
    </div>
  </div>;


const HeroSection: React.FC<HeroSectionProps> = memo(({ scrollToSection, onOpenBooking, overrideTitle, overrideSubtitle, overrideDescription, overrideCta, overrideBanner, overrideBannerMobile, hideSecondaryButton }) => {
  const { ref: parallaxRef } = useParallax({ speed: 0.15 });
  const heroTextRef = useRef<HTMLDivElement>(null);
  const heroScope = useRef<any>(null);

  useEffect(() => {
    const el = heroTextRef.current;
    if (!el) return;
    let cancelled = false;
    import('animejs').then(({ animate, stagger, createScope }) => {
      if (cancelled) return;
      heroScope.current = createScope({ root: el }).add(() => {
        animate('.hero-item', {
          opacity: [0, 1],
          translateY: [28, 0],
          duration: 800,
          ease: 'outExpo',
          delay: stagger(120, { start: 200 }),
        });
      });
    });
    return () => { cancelled = true; heroScope.current?.revert(); };
  }, []);

  return (
    <section ref={parallaxRef} className="pt-16 lg:pt-24 lg:min-h-screen relative overflow-hidden bg-dark-base">
      
      
      {/* Layout Mobile/Tablet - Vertical */}
      <div className="lg:hidden flex flex-col">
        {/* Imagem no topo - LCP Element */}
        <figure className="w-full relative m-0">
          <picture>
            <img
              src={overrideBannerMobile ?? heroBannerMobile}
              alt="Dr. Luis Brunello, cirurgião plástico em Curitiba, especialista em cirurgia das mamas e contorno corporal"
              title="Dr. Luis Brunello — Cirurgião Plástico em Curitiba"
              className="w-full h-auto object-cover"
              width="768"
              height="768"
              // @ts-ignore -- React 18 doesn't type fetchpriority but browsers need it
              fetchpriority="high"
              decoding="sync"
              loading="eager"
              sizes="100vw"
            />
          </picture>
        </figure>

        {/* Texto centralizado abaixo */}
        <div className="px-6 pt-8 pb-12 text-center bg-dark-base relative">
          <div className="space-y-6">
            <header className="space-y-4">
              {/* Mobile: visual title como <p>. O <h1> semântico vive no layout desktop e é único na página (mesmo com wrappers responsivos, crawlers ignoram CSS). */}
              <p className="text-3xl text-white leading-tight tracking-tight font-display">
                {overrideTitle ?? (<>Cirurgia Plástica com <br /><span className="text-gradient">Planejamento e Segurança</span></>)}
              </p>
              <p className="text-lg font-medium text-white tracking-wide">
                {overrideSubtitle ?? 'Dr. Luis Brunello • Cirurgião plástico em Curitiba'}
              </p>
              <p className="text-base text-white/90 leading-relaxed">
                {overrideDescription ?? (<>A cirurgia plástica vai além da estética. Ela envolve planejamento cuidadoso, compreensão das expectativas do paciente e respeito às características individuais do corpo.
                <br /><br />
                Meu trabalho é conduzir cada procedimento com critério técnico, explicação clara e acompanhamento próximo em todas as etapas, desde a primeira consulta até muito depois do pós operatório.</>)}
              </p>
            </header>
            <div className="flex flex-col gap-4">
              <Button
                onClick={onOpenBooking}
                size="lg"
                className="btn-pill btn-gold text-base font-semibold"
                 aria-label={overrideCta ?? "Agendar Consulta"}>

                {overrideCta ?? 'Agendar Consulta'}
               </Button>
            </div>

            {/* Social Proof Mobile */}
            <SocialProof />
          </div>
        </div>
      </div>

      {/* Layout Desktop - Original com imagem de fundo e parallax */}
      <div
        className="hidden lg:flex min-h-[100vh] items-center relative">

        {/* Background com parallax - LCP Element */}
        <picture>
          <img
            src={overrideBanner ?? heroBanner}
            alt=""
            aria-hidden="true"
            title="Dr. Luis Brunello — Cirurgião Plástico em Curitiba"
            data-parallax-target
            className="absolute inset-0 w-full h-full object-cover object-[right_top] will-change-transform"
            width="1920"
            height="1080"
            // @ts-ignore -- React 18 doesn't type fetchpriority but browsers need it
            fetchpriority="high"
            decoding="sync"
            loading="eager"
            sizes="100vw"
          />
        </picture>

        {/* Gradient overlay — garante legibilidade em banners claros ou escuros */}
        <div className="absolute inset-0 bg-gradient-to-r from-dark-base/90 via-dark-base/60 to-transparent pointer-events-none" aria-hidden="true" />

        <div className="container mx-auto px-6 lg:px-12 relative z-10" ref={heroTextRef}>
          <div className="w-full lg:max-w-sm xl:max-w-xl 2xl:max-w-2xl">
            <div className="space-y-8">
              <header className="space-y-6">
                {/* H1 único e canônico da página. Aparece em ambos viewports via wrapper responsivo, mas só este é semanticamente <h1> — o mobile renderiza um <p> visual idêntico. */}
                <h1
                  className="hero-item text-4xl lg:text-5xl font-playfair font-bold text-light-primary leading-tight tracking-tight"
                  style={{ opacity: 0 }}
                >
                  {overrideTitle ?? (<>Cirurgia Plástica com <br /><span className="text-gradient">Planejamento e Segurança</span></>)}
                </h1>
                <p className="hero-item text-xl font-medium text-white tracking-wide" style={{ opacity: 0 }}>
                  {overrideSubtitle ?? 'Dr. Luis Brunello • Cirurgião plástico em Curitiba'}
                </p>
                <p className="hero-item text-xl text-white/90 leading-relaxed max-w-xl" style={{ opacity: 0 }}>
                  {overrideDescription ?? (<>A cirurgia plástica vai além da estética. Ela envolve planejamento cuidadoso, compreensão das expectativas do paciente e respeito às características individuais do corpo.
                  <br /><br />
                  Meu trabalho é conduzir cada procedimento com critério técnico, explicação clara e acompanhamento próximo em todas as etapas, desde a primeira consulta até muito depois do pós operatório.</>)}
                </p>
              </header>
              <div className="hero-item flex flex-col sm:flex-row gap-4" style={{ opacity: 0 }}>
                <Button
                  onClick={onOpenBooking}
                  size="lg"
                  className="btn-pill btn-gold text-base font-semibold"
                  aria-label={overrideCta ?? "Agendar Consulta"}>

                  {overrideCta ?? 'Agendar Consulta'}
                </Button>
                {!hideSecondaryButton && (
                  <Button
                    onClick={() => scrollToSection('sobre')}
                    size="lg"
                    className="btn-pill btn-gold text-base font-semibold"
                    aria-label="Conheça Minha Trajetória">

                    Conheça Minha Trajetória
                  </Button>
                )}
              </div>

              {/* Social Proof Desktop */}
              <div className="hero-item" style={{ opacity: 0 }}>
                <SocialProof />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

});

HeroSection.displayName = 'HeroSection';

export default HeroSection;
